import { createBrowserRouter } from 'react-router-dom';

import { route_path } from '@app/utils/route_path';
import { WithoutHeaderLayout } from '@app/components/layout';
import { LoginPage, MainPage, RedirectPaymentPage } from '@app/pages';
import { RootLayout, DefaultLayout, StageLayout, CommonOutlet } from '@app/app/layouts';

import { createLangRoutes, loadWithSuspense } from './lib';
import {
  LanguageSyncWrapper,
  LocalizedNavigate,
  ErrorBoundaryLayout,
  WorkspaceStageLayout,
  AiChatStageLayout,
  AiChatPreviewLayout,
} from './ui';
import { BoxedMainPageGuard, BoxedRouteGuard } from './guards';
import {
  FinalizeStagePage,
  SharingPage,
  TextToSlidesPage,
  TextToSlidesPreviewPage,
  NewChatPage,
  RegistrationPage,
  LegalPage,
  TermsPage,
  PrivacyPage,
  CookiePage,
  FeaturesPage,
  AboutPage,
  PricingPage,
  PasswordReset,
  PasswordResetConfirm,
  Error404Page,
  CanvasDemoPage,
  ProjectsPage,
  SetupStylePage,
  DomEditorPage,
} from './config';

export const router = () =>
  createBrowserRouter([
    {
      element: <ErrorBoundaryLayout />,
      children: [
        {
          element: <RootLayout />,
          children: [
            {
              path: route_path.main,
              element: (
                <LanguageSyncWrapper>
                  <CommonOutlet />
                </LanguageSyncWrapper>
              ),
              children: [
                {
                  index: true,
                  element: loadWithSuspense(
                    <BoxedMainPageGuard>
                      <MainPage />
                    </BoxedMainPageGuard>,
                  ),
                },
                {
                  path: ':lang',
                  element: loadWithSuspense(
                    <BoxedMainPageGuard>
                      <MainPage />
                    </BoxedMainPageGuard>,
                  ),
                },
                {
                  path: ':lang/',
                  element: loadWithSuspense(
                    <BoxedMainPageGuard>
                      <MainPage />
                    </BoxedMainPageGuard>,
                  ),
                },
                ...createLangRoutes(route_path.login, loadWithSuspense(<LoginPage />)),
                ...createLangRoutes(route_path.register, loadWithSuspense(<RegistrationPage />)),
                ...createLangRoutes(route_path.passwordReset, loadWithSuspense(<PasswordReset />)),
                // Password reset with token - нужен отдельный подход, так как есть параметр :token
                {
                  path: `${route_path.passwordReset}:token`,
                  element: loadWithSuspense(<PasswordResetConfirm />),
                },
                {
                  path: `/:lang${route_path.passwordReset}:token`,
                  element: loadWithSuspense(<PasswordResetConfirm />),
                },
                {
                  path: route_path.main,
                  element: <WithoutHeaderLayout />,
                  children: [
                    ...createLangRoutes(
                      route_path.about,
                      loadWithSuspense(
                        <BoxedRouteGuard>
                          <AboutPage />
                        </BoxedRouteGuard>,
                      ),
                    ),
                    ...createLangRoutes(
                      route_path.features,
                      loadWithSuspense(
                        <BoxedRouteGuard>
                          <FeaturesPage />
                        </BoxedRouteGuard>,
                      ),
                    ),
                  ],
                },

                /***** Default pages *****/
                {
                  path: route_path.main,
                  element: <DefaultLayout />,
                  children: [
                    ...createLangRoutes(
                      route_path.pricing,
                      loadWithSuspense(
                        <BoxedRouteGuard>
                          <PricingPage />
                        </BoxedRouteGuard>,
                      ),
                    ),
                    // Автоматический redirect на text-to-slides чат
                    {
                      path: route_path.create,
                      element: <LocalizedNavigate to={route_path.textToSlides} replace />,
                    },
                    {
                      path: `/:lang${route_path.create}`,
                      element: <LocalizedNavigate to={route_path.textToSlides} replace />,
                    },
                    {
                      path: route_path.workspaceCreate,
                      element: <LocalizedNavigate to={route_path.textToSlides} replace />,
                    },
                    {
                      path: `/:lang${route_path.workspaceCreate}`,
                      element: <LocalizedNavigate to={route_path.textToSlides} replace />,
                    },
                    ...createLangRoutes(route_path.projects, loadWithSuspense(<ProjectsPage />)),
                    ...createLangRoutes(
                      '/canvas-demo/',
                      loadWithSuspense(
                        <BoxedRouteGuard>
                          <CanvasDemoPage />
                        </BoxedRouteGuard>,
                      ),
                    ),
                    // Redirect payment - нужен отдельный подход из-за wildcard
                    {
                      path: `${route_path.redirectPayment}*`,
                      element: loadWithSuspense(<RedirectPaymentPage />),
                    },
                    {
                      path: `/:lang${route_path.redirectPayment}*`,
                      element: loadWithSuspense(<RedirectPaymentPage />),
                    },
                    ...createLangRoutes(route_path.notFound, loadWithSuspense(<Error404Page />)),
                  ],
                },

                /***** Sharing *****/
                { path: '/s/:token', element: loadWithSuspense(<SharingPage />) },

                /***** Stage *****/
                {
                  path: route_path.main,
                  element: <StageLayout />,
                  children: [
                    // Пути с параметрами - создаем вручную
                    {
                      path: `${route_path.setupStyle}:projectId/`,
                      element: loadWithSuspense(<SetupStylePage />),
                    },
                    {
                      path: `/:lang${route_path.setupStyle}:projectId/`,
                      element: loadWithSuspense(<SetupStylePage />),
                    },
                  ],
                },

                /***** Stage Workspace *****/
                {
                  path: route_path.main,
                  element: <WorkspaceStageLayout />,
                  children: [
                    // Автоматический redirect на text-to-slides чат (workspace upload)
                    {
                      path: route_path.workspaceUploadDraft,
                      element: loadWithSuspense(
                        <LocalizedNavigate to={route_path.textToSlides} replace />,
                      ),
                    },
                    {
                      path: `/:lang${route_path.workspaceUploadDraft}`,
                      element: loadWithSuspense(
                        <LocalizedNavigate to={route_path.textToSlides} replace />,
                      ),
                    },

                    {
                      path: `${route_path.workspaceSetupStyle}:workspaceId`,
                      element: loadWithSuspense(<SetupStylePage isWorkspaceVariant />),
                    },
                    {
                      path: `/:lang${route_path.workspaceSetupStyle}:workspaceId`,
                      element: loadWithSuspense(<SetupStylePage isWorkspaceVariant />),
                    },
                    {
                      path: `${route_path.workspace}:workspaceId/`,
                      element: loadWithSuspense(<FinalizeStagePage />),
                    },
                    {
                      path: `/:lang${route_path.workspace}:workspaceId/`,
                      element: loadWithSuspense(<FinalizeStagePage />),
                    },
                  ],
                },

                /***** AI Chat Workspace (Text-to-Slides) *****/
                {
                  path: route_path.main,
                  element: <AiChatStageLayout />,
                  children: [
                    ...createLangRoutes(
                      route_path.textToSlides,
                      loadWithSuspense(<TextToSlidesPage />),
                    ),
                  ],
                },

                /***** AI Chat Preview (Text-to-Slides Preview) *****/
                {
                  path: route_path.main,
                  element: <AiChatPreviewLayout />,
                  children: [
                    {
                      path: `${route_path.textToSlidesPreview}:workspaceId/`,
                      element: loadWithSuspense(<TextToSlidesPreviewPage />),
                    },
                    {
                      path: `/:lang${route_path.textToSlidesPreview}:workspaceId/`,
                      element: loadWithSuspense(<TextToSlidesPreviewPage />),
                    },
                  ],
                },

                /***** New Chat Widget (dev server only) *****/
                // [TEMPORARY] Route is active only when running webpack-dev-server locally.
                // After the new chat backend is integrated into the main app,
                // replace this conditional block with a permanent route.
                ...(IS_DEV_SERVER
                  ? [
                      {
                        path: route_path.main,
                        element: <AiChatStageLayout />,
                        children: [
                          ...createLangRoutes(
                            route_path.newChat,
                            loadWithSuspense(<NewChatPage />),
                          ),
                        ],
                      },
                    ]
                  : []),

                /***** DOM-based Editor (dev server only) *****/
                ...(IS_DEV_SERVER
                  ? [
                      {
                        path: route_path.main,
                        element: <DefaultLayout />,
                        children: [
                          ...createLangRoutes(
                            route_path.domEditor,
                            loadWithSuspense(<DomEditorPage />),
                          ),
                        ],
                      },
                    ]
                  : []),

                /***** Legal *****/
                {
                  path: route_path.legal,
                  element: <DefaultLayout />,
                  children: [
                    {
                      index: true,
                      element: loadWithSuspense(
                        <BoxedRouteGuard>
                          <LegalPage />
                        </BoxedRouteGuard>,
                      ),
                    },
                    {
                      path: 'terms/',
                      element: loadWithSuspense(
                        <BoxedRouteGuard>
                          <TermsPage />
                        </BoxedRouteGuard>,
                      ),
                    },
                    {
                      path: 'cookie/',
                      element: loadWithSuspense(
                        <BoxedRouteGuard>
                          <CookiePage />
                        </BoxedRouteGuard>,
                      ),
                    },
                    {
                      path: 'privacy/',
                      element: loadWithSuspense(
                        <BoxedRouteGuard>
                          <PrivacyPage />
                        </BoxedRouteGuard>,
                      ),
                    },
                  ],
                },
                {
                  path: `/:lang${route_path.legal}`,
                  element: <DefaultLayout />,
                  children: [
                    {
                      index: true,
                      element: loadWithSuspense(
                        <BoxedRouteGuard>
                          <LegalPage />
                        </BoxedRouteGuard>,
                      ),
                    },
                    {
                      path: 'terms/',
                      element: loadWithSuspense(
                        <BoxedRouteGuard>
                          <TermsPage />
                        </BoxedRouteGuard>,
                      ),
                    },
                    {
                      path: 'cookie/',
                      element: loadWithSuspense(
                        <BoxedRouteGuard>
                          <CookiePage />
                        </BoxedRouteGuard>,
                      ),
                    },
                    {
                      path: 'privacy/',
                      element: loadWithSuspense(
                        <BoxedRouteGuard>
                          <PrivacyPage />
                        </BoxedRouteGuard>,
                      ),
                    },
                  ],
                },
              ],
            },

            /***** NotFound *****/
            { path: '*', element: loadWithSuspense(<Error404Page />) },
          ],
        },
      ],
    },
  ]);
